import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { useConversations } from '~/hooks/conversation/useConversations'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { ConversationsResponse } from '~/services/conversationService'
import { SocketEvent } from '~/services/socket'
import { ConversationWithTasks } from '~/types/Conversations'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

// Type for the conversations data that will be shared
export interface ConversationsData {
  conversations: ConversationWithTasks[]
  fetchNextPage: () => Promise<ConversationsResponse>
  hasNextPage: boolean | undefined
  isFetching: boolean
  isFetchingNextPage: boolean
  error: Error | null
  status: string
  isLoading: boolean
  refetch: () => Promise<unknown>
  updateConversation: (identifier: string | number) => Promise<void>
  addConversation: (conversation: ConversationWithTasks) => void
  upsertConversation: (conversation: ConversationWithTasks) => void
  markConversationDeleted: (uuid: string) => void // Mark a conversation as deleted to prevent WebSocket update attempts
  searchQuery: string
  setSearchQuery: (q: string) => void
  searchType: 'chats' | 'tasks' | undefined
  setSearchType: (type: 'chats' | 'tasks' | undefined) => void
  // Credit limit enforcement
  dataHidden: boolean
  dataHiddenReason: 'credit_limit_exceeded' | null
  isFreeLicense: boolean
  creditsUsed: number
  creditsLimit: number
}

// Create the context
const ConversationContext = createContext<ConversationsData | null>(null)

// Provider component
interface ConversationProviderProps {
  children: ReactNode
}

export function ConversationProvider({ children }: ConversationProviderProps) {
  const {
    conversations,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    error,
    status,
    isLoading,
    refetch,
    updateConversation,
    addConversation,
    upsertConversation,
    mergeConversationFields,
    mergeTaskFields,
    setSearchQuery,
    searchQuery,
    setSearchType,
    searchType,
    // Credit limit enforcement
    dataHidden,
    dataHiddenReason,
    isFreeLicense,
    creditsUsed,
    creditsLimit
  } = useConversations()

  const { currentWorkspace } = useWorkspaceContext()

  // Track deleted conversation UUIDs to avoid processing stale WS events
  const deletedUuidsRef = useRef<Set<string>>(new Set())

  // Refs for stable access inside the WS handler closure
  const mergeConversationFieldsRef = useRef(mergeConversationFields)
  mergeConversationFieldsRef.current = mergeConversationFields
  const mergeTaskFieldsRef = useRef(mergeTaskFields)
  mergeTaskFieldsRef.current = mergeTaskFields
  const refetchRef = useRef(refetch)
  refetchRef.current = refetch

  // Clear deleted UUIDs when workspace changes — they belong to the old workspace
  useEffect(() => {
    deletedUuidsRef.current.clear()
  }, [currentWorkspace?.uuid])

  // Batched WS event processing — coalesce rapid-fire events into
  // a single state update per 500ms window to avoid thrashing React renders.
  const convBufferRef = useRef<Map<string, Record<string, unknown>>>(new Map())
  const taskBufferRef = useRef<
    Map<
      string,
      {
        task_id: number
        conversation_id: number
        status?: string
        cmd_type?: string
        updated_ts?: string
      }
    >
  >(new Map())
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flushBuffers = useCallback(() => {
    flushTimerRef.current = null
    for (const [, data] of convBufferRef.current) {
      mergeConversationFieldsRef.current(
        data as {
          uuid?: string
          id?: number
          title?: string
          conversation_metadata?: Record<string, unknown>
          created_time?: string
          updated_time?: string
        }
      )
    }
    convBufferRef.current.clear()

    for (const [, data] of taskBufferRef.current) {
      mergeTaskFieldsRef.current(data)
    }
    taskBufferRef.current.clear()
  }, [])

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return
    flushTimerRef.current = setTimeout(flushBuffers, 500)
  }, [flushBuffers])

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
    }
  }, [])

  // Listen for WebSocket events and buffer optimistic state merges.
  // Zero API calls — we use the data already in the WS event payload.
  useEffect(() => {
    const onSocketEvent = (...args: unknown[]) => {
      const data = args[0] as {
        event: string
        data: Record<string, unknown>
      }

      if (data.event === SocketEvent.ConversationUpdated) {
        const eventUuid = data.data?.uuid as string | undefined
        if (eventUuid && deletedUuidsRef.current.has(eventUuid)) return

        if (eventUuid) {
          convBufferRef.current.set(eventUuid, data.data)
          scheduleFlush()
        } else {
          refetchRef.current().catch(error => {
            console.error('Failed to refetch conversations:', error)
          })
        }
      } else if (data.event === SocketEvent.TaskUpdated) {
        const conversationId = Number(data.data?.conversation_id)
        const taskId = Number(data.data?.task_id)
        if (!Number.isFinite(conversationId) || !Number.isFinite(taskId)) return

        taskBufferRef.current.set(`${conversationId}-${taskId}`, {
          task_id: taskId,
          conversation_id: conversationId,
          status: data.data?.status as string | undefined,
          cmd_type: data.data?.cmd_type as string | undefined,
          updated_ts: data.data?.updated_ts as string | undefined
        })
        scheduleFlush()
      }
    }

    globalEventEmitter.on(AppEvents.SocketEvent, onSocketEvent)
    return () => {
      globalEventEmitter.off(AppEvents.SocketEvent, onSocketEvent)
    }
  }, [scheduleFlush])

  useEffect(() => {
    const onSendMessage = (...args: unknown[]) => {
      const payload = (args[0] ?? {}) as {
        conversationUuid?: string
      }
      if (!payload.conversationUuid) return

      mergeConversationFieldsRef.current({
        uuid: payload.conversationUuid,
        forceTop: true
      })
    }

    globalEventEmitter.on(AppEvents.SendMessage, onSendMessage)
    return () => {
      globalEventEmitter.off(AppEvents.SendMessage, onSendMessage)
    }
  }, [])

  // Function to mark a conversation as deleted (called by ChatItem when deleting)
  // This prevents WebSocket events from trying to update the deleted conversation
  const markConversationDeleted = useCallback((uuid: string) => {
    deletedUuidsRef.current.add(uuid)
  }, [])

  // Adapt the fetchNextPage function to match expected interface
  const adaptedFetchNextPage = useCallback(async () => {
    const result = await fetchNextPage()
    // fetchNextPage already returns a ConversationsResponse or undefined
    if (!result) {
      return {
        data: [],
        next_cursor: null,
        has_more: false
      }
    }
    return result
  }, [fetchNextPage])

  const contextValue = useMemo<ConversationsData>(
    () => ({
      conversations,
      fetchNextPage: adaptedFetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      error,
      status,
      isLoading,
      refetch,
      updateConversation,
      addConversation,
      upsertConversation,
      markConversationDeleted,
      searchQuery,
      setSearchQuery,
      searchType,
      setSearchType,
      dataHidden,
      dataHiddenReason,
      isFreeLicense,
      creditsUsed,
      creditsLimit
    }),
    [
      conversations,
      adaptedFetchNextPage,
      hasNextPage,
      isFetching,
      isFetchingNextPage,
      error,
      status,
      isLoading,
      refetch,
      updateConversation,
      addConversation,
      upsertConversation,
      markConversationDeleted,
      searchQuery,
      setSearchQuery,
      searchType,
      setSearchType,
      dataHidden,
      dataHiddenReason,
      isFreeLicense,
      creditsUsed,
      creditsLimit
    ]
  )

  return (
    <ConversationContext.Provider value={contextValue}>{children}</ConversationContext.Provider>
  )
}

// Hook to use the context
export function useConversationsContext(): ConversationsData {
  const context = useContext(ConversationContext)
  if (!context) {
    throw new Error('useConversationsContext must be used within a ConversationProvider')
  }
  return context
}

// Export the context for testing purposes
export { ConversationContext }
